import { useCallback, useEffect } from "react";
import {
  loadFirstVisitTimestamp,
  loadLastDailyVisit,
  setFirstVisitTimestamp,
  setLastDailyVisit
} from "@/common/utils/storage";
import { bus } from "@/common/buses/metrics/bus";
import { useUserStore } from "@/common/store";
import { useDeviceType } from "@/common/hooks";
import sessionService from "@/common/services/session/sessionService";
import microsoftClarity from "@/common/services/microsoftClarity/microsoftClarity";

interface IDailyVisit {
  timestamp: number;
  authorized: boolean;
}

export const useTrackVisit = () => {
  const user = useUserStore((state) => state.currentUser);
  const setSessionId = useUserStore((state) => state.setSessionId);
  const deviceType = useDeviceType();

  const trackDailyVisit = useCallback((username?: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayTimestamp = today.getTime();
    const lastVisit = loadLastDailyVisit<IDailyVisit>();

    if (lastVisit && lastVisit.timestamp >= todayTimestamp) {
      if (!username || lastVisit.authorized) {
        return; // track anonymous users once per day and again when the user logs in.
      }
    }

    bus.emit("dailyVisit", { username });

    setLastDailyVisit({
      timestamp: todayTimestamp,
      authorized: !!username
    });
  }, []);

  useEffect(() => {
    if (!loadFirstVisitTimestamp()) {
      const msInSec = 1000;
      const timestamp = Math.floor(Date.now() / msInSec);

      bus.emit("firstVisit", { path: window.location.pathname, timestamp });
      setFirstVisitTimestamp(timestamp);
    }

    (async () => {
      const sessionId = (await sessionService.getSession()).session;
      setSessionId(sessionId);
      microsoftClarity.startTracking();

      bus.emit("deviceTracked", { deviceId: sessionId, deviceType });
    })();

    trackDailyVisit();
  }, []);

  useEffect(() => {
    if (user?.username) {
      trackDailyVisit(user.username);
    }
  }, [user]);
};
